import { useMemo, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { MagnifyingGlass } from '@phosphor-icons/react'
import { istDateKey } from '@/lib/ist'
import { cn } from '@/lib/cn'
import { Banner, Field, Input, Select, Spinner } from '@/ui'
import { gramsToKgString } from '../../api/mappers'
import {
  useAllSkus,
  useApprovedLabels,
  useHallEquipment,
  usePackableBatchByCode,
  usePackableBatches,
  usePackingHalls,
  usePlanPackRun,
  type PackableBatch,
} from '../../api/packFull'
import type { UserOption } from '../../api/reference'
import { FormDialog } from '../FormDialog'
import { PersonPicker } from '../PersonPicker'
import { Kg, Status } from '../bits'
import { RefusalBanner } from './Refusal'
import { batchReasonText, labStatusText } from '../../model/pack/packWords'
import {
  emptyPlanDraft,
  kgTextToGrams,
  planBody,
  planOk,
  plannedPouches,
  validatePlanDraft,
  type PlanDraft,
} from '../../model/pack/runForms'

/** Plan a pack run (F7.1): product, batch, kilos, hall, line, operator, day. */
export function PlanRunDialog({ open, onClose }: { open: boolean; onClose: () => void }) {
  if (!open) return null
  return <PlanRunForm onClose={onClose} />
}

function PlanRunForm({ onClose }: { onClose: () => void }) {
  const today = istDateKey()
  const navigate = useNavigate()
  const [d, setD] = useState<PlanDraft>(emptyPlanDraft(today))
  const [operator, setOperator] = useState<UserOption | null>(null)
  const [tried, setTried] = useState(false)
  const [lookup, setLookup] = useState('')
  const set = (patch: Partial<PlanDraft>) => setD((x) => ({ ...x, ...patch }))

  const skus = useAllSkus()
  const halls = usePackingHalls()
  const sku = skus.data?.find((s) => s.id === d.skuId) ?? null
  const hall = halls.data?.find((h) => h.id === d.locationId) ?? null
  const batches = usePackableBatches(d.skuId || null)
  const byCode = usePackableBatchByCode(lookup)
  const equipment = useHallEquipment(d.locationId || null)
  const labels = useApprovedLabels(d.skuId || null, d.locationId || null)
  const plan = usePlanPackRun()

  const lines = useMemo(() => {
    const all = (equipment.data ?? []).filter((e) => !e.isScale)
    const pack = all.filter((e) => e.isPackLine)
    return pack.length > 0 ? pack : all
  }, [equipment.data])

  const offered = batches.data ?? []
  const looked = byCode.data ?? null
  const lookedExtra = looked && !offered.some((b) => b.unitId === looked.unitId) ? looked : null
  const all = lookedExtra ? [...offered, lookedExtra] : offered
  const batch = all.find((b) => b.unitId === d.batchUnitId) ?? null

  const grams = kgTextToGrams(d.plannedKg)
  const pouches = plannedPouches(grams, sku?.netG)
  const p = validatePlanDraft(d, {
    availableG: batch?.availableG ?? null,
    netG: sku?.netG ?? null,
    today,
  })
  const shown = (k: keyof PlanDraft) => (tried ? p[k] : undefined)

  const notPickable = (b: PackableBatch): string[] => {
    const why = b.eligible ? [] : b.reasons.map(batchReasonText)
    if (sku && b.cropId && b.cropId !== sku.cropId)
      why.push('It is of another crop than this product.')
    return why
  }

  const pickBatch = (b: PackableBatch) => {
    set({ batchUnitId: b.unitId, plannedKg: gramsToKgString(b.availableG) })
  }

  const today0 = d.plannedDate || today
  const licenceOk = hall
    ? Boolean(hall.fssaiLicenceNo && hall.fssaiLicenceValidTo && hall.fssaiLicenceValidTo >= today0)
    : null

  return (
    <FormDialog
      open
      onClose={onClose}
      title="Plan a pack run"
      description="Only batches from one farmer, whose machine run balanced, with a sealed quality sample and not on hold can be packed. The lab result may still be pending; release waits for it."
      saveLabel="Create pack run"
      loading={plan.isPending}
      onSave={async () => {
        setTried(true)
        if (!planOk(p)) throw new Error('invalid')
        const r = await plan.mutateAsync(planBody(d))
        navigate(`/c/pack-runs/${r.id}`)
      }}
    >
      {plan.isError && <RefusalBanner error={plan.error} title="The pack run was not created" />}

      <Field
        label="Product"
        required
        error={shown('skuId')}
        hint={sku ? `${sku.cropName ?? 'Crop'} · ${sku.netG} g pouches` : undefined}
      >
        <Select
          value={d.skuId}
          onChange={(skuId) => set({ skuId, batchUnitId: '', plannedKg: '' })}
          placeholder={skus.isLoading ? 'Loading…' : 'Pick the product'}
          options={(skus.data ?? []).map((s) => ({ value: s.id, label: `${s.code} · ${s.name}` }))}
          data-autofocus
        />
      </Field>
      {sku && !sku.shelfLifeDays && (
        <Banner tone="warn" title="This product has no shelf life set">
          Set it under SKUs first — the best-before date is worked out from it.
        </Banner>
      )}

      {d.skuId && (
        <fieldset className="flex flex-col gap-2">
          <legend className="mb-1 text-[14px] font-semibold text-ink">
            Batch{' '}
            <span className="text-danger" aria-hidden="true">
              *
            </span>
          </legend>
          {batches.isLoading ? (
            <div className="flex items-center gap-2 text-[14px] text-muted">
              <Spinner /> Finding batches…
            </div>
          ) : batches.isError ? (
            <RefusalBanner error={batches.error} title="Could not list the batches" />
          ) : offered.length === 0 ? (
            <p className="text-[14px] text-muted">
              No batch of this crop can be packed right now. Look one up by its code to see why.
            </p>
          ) : null}
          <div
            role="radiogroup"
            aria-label="Batch"
            className="flex max-h-72 flex-col gap-2 overflow-auto"
          >
            {all.map((b) => (
              <BatchOption
                key={b.unitId}
                b={b}
                selected={b.unitId === d.batchUnitId}
                blocked={notPickable(b)}
                onPick={() => pickBatch(b)}
              />
            ))}
          </div>
          {shown('batchUnitId') && (
            <p className="text-[13px] font-medium text-danger">{p.batchUnitId}</p>
          )}
          <label className="mt-1 flex flex-col gap-1">
            <span className="text-[13px] text-muted">Batch not listed? Look it up by code</span>
            <Input
              value={lookup}
              onChange={(e) => setLookup(e.target.value.toUpperCase())}
              placeholder="Batch code"
              leading={<MagnifyingGlass />}
              trailing={byCode.isFetching ? <Spinner /> : undefined}
              className="font-mono"
            />
          </label>
          {byCode.isError && lookup.trim().length >= 3 && (
            <p className="text-[13px] text-warn">No batch with that code.</p>
          )}
        </fieldset>
      )}

      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
        <Field
          label="Kilos to pack"
          required
          error={
            shown('plannedKg') ??
            (grams !== null && batch && grams > batch.availableG ? p.plannedKg : undefined)
          }
          hint={
            batch ? (
              <span>
                Up to <Kg grams={batch.availableG} /> available
                {pouches !== null ? ` · ${pouches.toLocaleString('en-IN')} pouches` : ''}
              </span>
            ) : (
              'Defaults to the whole batch'
            )
          }
        >
          <Input
            inputMode="decimal"
            value={d.plannedKg}
            onChange={(e) => set({ plannedKg: e.target.value.replace(/[^\d.,]/g, '') })}
            trailing="kg"
            className="font-mono"
          />
        </Field>
        <Field label="Planned day" required error={shown('plannedDate')}>
          <Input
            type="date"
            value={d.plannedDate}
            min={today}
            onChange={(e) => set({ plannedDate: e.target.value })}
          />
        </Field>
        <Field
          label="Packing hall"
          required
          error={shown('locationId')}
          hint={
            hall
              ? licenceOk
                ? `FSSAI licence ${hall.fssaiLicenceNo} · valid to ${hall.fssaiLicenceValidTo}`
                : undefined
              : undefined
          }
        >
          <Select
            value={d.locationId}
            onChange={(locationId) => set({ locationId, equipmentId: '' })}
            placeholder={halls.isLoading ? 'Loading…' : 'Pick the hall'}
            options={(halls.data ?? []).map((h) => ({ value: h.id, label: h.name }))}
          />
        </Field>
        <Field label="Pack line" required error={shown('equipmentId')}>
          <Select
            value={d.equipmentId}
            onChange={(equipmentId) => set({ equipmentId })}
            disabled={!d.locationId}
            placeholder={
              !d.locationId
                ? 'Pick the hall first'
                : equipment.isLoading
                  ? 'Loading…'
                  : lines.length
                    ? 'Pick the line'
                    : 'No equipment at this hall'
            }
            options={lines.map((e) => ({ value: e.id, label: `${e.code} · ${e.name}` }))}
          />
        </Field>
      </div>
      {hall && licenceOk === false && (
        <Banner tone="error" title="This hall has no FSSAI licence in force on that day">
          Add or renew the licence on the hall under Locations first.
        </Banner>
      )}
      {d.skuId && d.locationId && labels.data && labels.data.length === 0 && (
        <Banner tone="warn" title="No approved label for this product at this hall">
          Approve one under Labels first; the run cannot be created without it.
        </Banner>
      )}
      <Field
        label="Pack operator"
        required
        error={shown('operatorId')}
        hint="The person who runs the line. They can never sign this lot's release."
      >
        <PersonPicker
          value={operator}
          onChange={(u) => {
            setOperator(u)
            set({ operatorId: u?.id ?? '' })
          }}
          label="Pack operator"
          invalid={Boolean(shown('operatorId'))}
        />
      </Field>
      {batch && batch.sacksLocationCodes.length > 0 && (
        <p className="text-[13px] text-muted">
          After planning, ask the store keeper to issue the batch's sacks to this run (now at{' '}
          {batch.sacksLocationCodes.join(', ')}).
        </p>
      )}
    </FormDialog>
  )
}

function BatchOption({
  b,
  selected,
  blocked,
  onPick,
}: {
  b: PackableBatch
  selected: boolean
  blocked: string[]
  onPick: () => void
}) {
  const disabled = blocked.length > 0
  return (
    <button
      type="button"
      role="radio"
      aria-checked={selected}
      aria-disabled={disabled || undefined}
      disabled={disabled}
      onClick={onPick}
      className={cn(
        'flex flex-col gap-1 rounded-card border px-3 py-2 text-left text-[14px] transition-colors',
        selected ? 'border-ink bg-paper-tint' : 'border-rule bg-surface hover:border-ink/40',
        disabled && 'cursor-not-allowed opacity-70 hover:border-rule',
      )}
    >
      <span className="flex flex-wrap items-center gap-x-3 gap-y-1">
        <span className="font-mono text-[13.5px] font-semibold">{b.code}</span>
        <Kg grams={b.availableG} />
        <span className="text-muted">
          {b.sacks} {b.sacks === 1 ? 'sack' : 'sacks'}
          {b.sacksLocationCodes.length > 0 ? ` at ${b.sacksLocationCodes.join(', ')}` : ''}
        </span>
        <span className="ml-auto">
          <Status status={b.labStatus ?? 'PENDING'} label={labStatusText(b.labStatus)} />
        </span>
      </span>
      {(b.parentLotCodes.length > 0 || b.leftoverOfBatchCode) && (
        <span className="text-[12.5px] text-muted">
          {b.leftoverOfBatchCode ? `Left over from pack runs of ${b.leftoverOfBatchCode} · ` : ''}
          {b.parentLotCodes.length > 0 ? `From lot ${b.parentLotCodes.join(', ')}` : ''}
        </span>
      )}
      {disabled && (
        <ul className="list-disc pl-5 text-[12.5px] text-danger">
          {blocked.map((r) => (
            <li key={r}>{r}</li>
          ))}
        </ul>
      )}
    </button>
  )
}
